/**
 * Captcha proof-of-work (Cap, auto-hébergé) sur les endpoints d'auth. Le widget
 * tourne en mode programmatique : il résout un challenge côté client (CPU) puis
 * nous donne un cap-token à usage unique, rejoué dans l'en-tête `X-Captcha-Token`.
 *
 * On centralise tout ici pour que `features/auth/api.ts` enveloppe simplement
 * ses appels — les pages et le store n'ont rien à savoir du captcha.
 */
import Cap from '@cap.js/widget'
import type { AxiosRequestConfig } from 'axios'
import { extractApiCode } from './apiError'
import { env } from './env'

// Le proxy captcha de notre API. Le slash final est impératif : sans lui, le
// widget construit mal ses sous-appels /challenge et /redeem.
const CAP_API_ENDPOINT = `${env.apiUrl}/cap/`

/**
 * Résout un nouveau challenge et renvoie le cap-token à usage unique. Renvoie
 * `null` quand le proxy captcha est indisponible (captcha pas encore activé
 * côté serveur, ou serveur Cap injoignable) : on laisse alors le back décider —
 * il accepte la requête tant que le captcha n'est pas activé, et la refuse (503)
 * une fois activé. Le back reste le point d'enforcement, donc ce fail-open
 * client n'ouvre aucune faille.
 */
async function solveCaptcha(): Promise<string | null> {
  try {
    const cap = new Cap({ apiEndpoint: CAP_API_ENDPOINT })
    const { success, token } = await cap.solve()
    return success ? token : null
  } catch {
    return null
  }
}

function captchaConfig(token: string | null): AxiosRequestConfig | undefined {
  return token ? { headers: { 'X-Captcha-Token': token } } : undefined
}

/**
 * Exécute un appel d'auth protégé : résout un token frais juste avant l'appel
 * et le transmet via `X-Captcha-Token`. Le token étant à usage unique, sur un
 * 403 CAPTCHA_FAILED (token rejeté ou expiré) on régénère un token et on rejoue
 * l'appel une seule fois.
 */
export async function withCaptcha<T>(
  call: (config?: AxiosRequestConfig) => Promise<T>,
): Promise<T> {
  const token = await solveCaptcha()
  try {
    return await call(captchaConfig(token))
  } catch (err) {
    if (extractApiCode(err) !== 'CAPTCHA_FAILED') throw err
    const retryToken = await solveCaptcha()
    return call(captchaConfig(retryToken))
  }
}
